import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../../utils/echarts'
import { Icon } from '../../../../../atoms/Icon'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import { Badge } from '../../../../../atoms/Badge'
import {
  Card,
  CardHeader,
  CardTitle,
  ChartContainer,
} from '../../../BuildingCharacteristics.styles'
import { buildTimelineEvents } from '../utils'
import { TIMELINE_COLORS } from '../ConstructionTimelineTab.constants'
import type { TimelineChartProps } from '../ConstructionTimelineTab.types'

export interface TimelineChartRef {
  exportChart: () => string | null
}

export const TimelineChart = forwardRef<TimelineChartRef, TimelineChartProps>(
  (
    {
      buildingYear,
      renovationYear,
      constructionDates,
      dwellingYears,
      isAgeEstimate,
      currentYear,
      t,
    },
    ref
  ) => {
    const timelineChartRef = useRef<HTMLDivElement>(null)
    const chartInstance = useRef<echarts.ECharts | null>(null)

    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (chartInstance.current) {
          return chartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        return null
      },
    }))

    useEffect(() => {
      if (!timelineChartRef.current || buildingYear === 0) return

      const chart = safeChartInit(timelineChartRef.current)
      chartInstance.current = chart
      const events = buildTimelineEvents(
        buildingYear,
        renovationYear,
        constructionDates,
        dwellingYears,
        isAgeEstimate,
        currentYear,
        t
      )

      const option = {
        animation: true,
        grid: {
          left: '10%',
          right: '10%',
          bottom: '15%',
          top: '10%',
        },
        xAxis: {
          type: 'value',
          name: t('timeline.chart.xAxisLabel'),
          min: Math.min(...events.map((e) => e.year)) - 5,
          max: currentYear + 2,
          axisLabel: {
            formatter: '{value}',
          },
        },
        yAxis: {
          type: 'category',
          data: [t('timeline.chart.yAxisLabel')],
          axisLabel: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            const event = events[params.dataIndex]
            const age = currentYear - event.year
            return `<strong>${event.label}</strong><br/>${t('timeline.chart.tooltipYear')}: ${event.year}${age > 0 ? `<br/>${t('timeline.chart.tooltipAge')}: ${age} ${t('common.years')}` : ''}`
          },
        },
        series: [
          {
            type: 'scatter',
            symbolSize: 60,
            data: events.map((event) => ({
              value: [event.year, 0],
              itemStyle: {
                color: event.color,
              },
            })),
            label: {
              show: true,
              position: 'top',
              formatter: (params: any) => events[params.dataIndex].year,
              fontSize: 12,
              fontWeight: 'bold',
            },
          },
          {
            type: 'line',
            data: events.map((event) => [event.year, 0]),
            lineStyle: {
              width: 2,
              color: TIMELINE_COLORS.TIMELINE_LINE,
              type: 'solid',
            },
            symbol: 'none',
            z: -1,
          },
        ],
      }

      chart.setOption(option)

      const handleResize = () => chart.resize()
      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
        chart.dispose()
        chartInstance.current = null
      }
    }, [
      buildingYear,
      renovationYear,
      currentYear,
      constructionDates,
      dwellingYears,
      isAgeEstimate,
      t,
    ])

    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <Icon name="clock" size="small" />
            {t('timeline.cards.timeline.title')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('timeline.cards.timeline.tooltip.title')}
                shortDescription={t(
                  'timeline.cards.timeline.tooltip.description'
                )}
                calculation={t('timeline.cards.timeline.tooltip.calculation')}
                importance={t('timeline.cards.timeline.tooltip.importance')}
                limitations={t('timeline.cards.timeline.tooltip.limitations')}
                source={t('timeline.cards.timeline.tooltip.source')}
              />
            </InfoTooltip>
          </CardTitle>
          {buildingYear > 0 && (
            <Badge variant="info">
              {isAgeEstimate
                ? t('timeline.badges.estimatedData')
                : t('timeline.badges.historicalData')}
            </Badge>
          )}
        </CardHeader>
        {buildingYear > 0 ? (
          <ChartContainer ref={timelineChartRef} />
        ) : (
          <div
            style={{
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
            }}
          >
            {t('timeline.noData')}
          </div>
        )}
      </Card>
    )
  }
)

TimelineChart.displayName = 'TimelineChart'
